/**
 * ENG-5452 / FE6 — the customer power user's org-wide engagements roll-up.
 *
 * Every engagement billed to an account the viewer owns or administers a team
 * on. The scope is the api's, derived from the viewer's own `team_members` rows
 * and never passed in, which is what makes it staff-proof: OF staff hold the
 * `*:*` wildcard, so any capability-shaped gate would hand them every account on
 * the platform.
 *
 * Pagination copies `StaffEngagementsList`, which copies `/expert_requests`:
 * cursor from the last edge, `LongList` with `hasNextPage` + `onMore`, pages
 * appended. Ordering is the server's, most recently active first, and there is
 * no sort control. "Needs your action" is a per-row chip rather than a sort
 * because the api has no such ordering to offer.
 *
 * Every filter narrows the QUERY. There is no client-side filtering at all: a
 * client-side predicate would narrow only the page in hand while `hasNextPage`
 * kept promising more, which is the same trap the staff feed's dropped sort
 * headers were.
 */
import { useQuery } from '@apollo/client';
import { useMemo, useRef, useState } from 'react';

import LongList from '@/components/LongList/LongList';
import Button from '@/componentsv2/Button';

import AccountRollupFilters from './AccountRollupFilters';
import AccountRollupRow from './AccountRollupRow';
import {
  DEFAULT_ROLLUP_FILTERS,
  type RollupFilterState,
  facetOptions,
  fromAccountEngagements,
  rollupFilterKey,
  rollupQueryVariables,
} from './accountRollupViewModel';
import { ACCOUNT_ENGAGEMENTS, ACCOUNT_ENGAGEMENT_FACETS } from './operations';

const PAGE_SIZE = 10;

/**
 * How many engagements the filter pickers read their options from. Generous
 * rather than exact: complete for an account with up to this many, which covers
 * the staged rollout (ENG-5446), and past it the pickers list the most recently
 * active and the bar says so.
 */
const FACET_PAGE_SIZE = 200;

const HEADER_CELL =
  'px-12 py-10 text-left util-caption font-medium uppercase tracking-wider text-on-surface-muted';

const COLUMNS = ['Project', 'Expert', 'Status', 'NTE (consumed)', 'Needs your action'];

export default function AccountEngagementsList(): JSX.Element {
  const [filters, setFilters] = useState<RollupFilterState>(DEFAULT_ROLLUP_FILTERS);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);

  // The pickers' options, fetched here rather than by the page: this is the only
  // surface that needs them, and the tab's gate is a separate COUNT so no viewer
  // pays for this page of rows until they actually open the roll-up.
  const facetQuery = useQuery(ACCOUNT_ENGAGEMENT_FACETS, {
    variables: { first: FACET_PAGE_SIZE },
  });
  const facets = facetOptions(facetQuery.data?.accountEngagements?.edges);
  const facetTotal = facetQuery.data?.accountEngagements?.pageInfo?.total ?? null;
  const optionsTruncated = facetTotal !== null && facetTotal > FACET_PAGE_SIZE;
  // An empty picker after a failed read says "this account has no experts",
  // which is a claim about the account rather than about the request. It also
  // suppresses the truncation note, so the one line that exists to stop silent
  // option loss is missing exactly when options are missing.
  const facetsFailed = facetQuery.error !== undefined;
  // The same claim while the read is still in flight, and it stands for LONGER
  // than the list's own first-page window below: the pickers read 200
  // engagements where the list reads 10, so every row can finish painting while
  // the pickers still tell the viewer their account has no experts — enabled,
  // and with neither the failure note nor the truncation note to explain them.
  const facetsPending = facetQuery.loading && facetQuery.data === undefined;

  // Keyed on the filter set's IDENTITY, not the object's reference.
  //
  // The pills call `onChange` unconditionally, including for the pill already
  // selected, so `filters` gets a fresh object on every click. Keying the memo
  // on that object re-ran `createdAfterIso(days, new Date())` and produced a
  // `createdAfter` differing by milliseconds, which Apollo reads as new
  // variables: it refetched from `after: null` and silently dropped every page
  // `handleMore` had appended. Clicking the pill you are already on collapsed
  // the list from thirty rows back to ten, but only with a date range active,
  // since `createdAfter` is otherwise null and the variables stay deep-equal.
  const filterKey = rollupFilterKey(filters);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `filterKey` IS the identity of `filters`.
  const facetVariables = useMemo(() => rollupQueryVariables(filters, new Date()), [filterKey]);
  const variables = { ...facetVariables, first: PAGE_SIZE, after: null as string | null };

  const { data, loading, error, refetch, fetchMore } = useQuery(ACCOUNT_ENGAGEMENTS, {
    variables,
    // network-only for the same reason as the staff feed: the merged multi-page
    // result is written back under the FIRST page's cache key, so a cache-first
    // paint on remount shows every page loaded last time and then collapses to
    // one page when the network reply lands, stranding the cursor in between.
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const edges = data?.accountEngagements?.edges ?? [];
  const hasNextPage = Boolean(data?.accountEngagements?.pageInfo?.hasNextPage);
  const total = data?.accountEngagements?.pageInfo?.total ?? null;
  const rows = fromAccountEngagements(edges);

  // The first page is still in flight, as distinct from a page that came back
  // with nothing in it. `LongList` renders its children unconditionally — it
  // consults `loading` only to suppress Load More and to pick a fallback title —
  // so without this branch the empty state IS the first paint, and the roll-up
  // opens by telling a power user their account has no matching engagements. On a
  // fast connection that is a flash; on a slow one it stands, and it is a claim
  // about the account rather than about the request.
  //
  // `notifyOnNetworkStatusChange` also raises `loading` for `fetchMore` and for a
  // filter change. Both are covered: a page in hand means `rows` is non-empty, and
  // a filter change empties `data` until the new page lands, which is the same
  // "nothing to show yet" state this branch exists for.
  const loadingFirstPage = loading && rows.length === 0;

  // The filter set as of the click, checked again when the page lands. Apollo's
  // `updateQuery` writes through the variables as of RESOLUTION, not of the
  // call, so paging on Active and then switching to Closed before it returns
  // would append Active's second page onto Closed's cache entry. Dropping the
  // late page is the honest outcome: the viewer is looking at a different
  // filter, and that filter's own `hasNextPage` is still true.
  const filterRef = useRef(filterKey);
  filterRef.current = filterKey;

  function handleFilterChange(next: RollupFilterState) {
    setLoadMoreFailed(false);
    setFilters(next);
  }

  function handleMore() {
    if (!hasNextPage || loading || edges.length === 0) return;
    const requestedFilter = filterKey;
    setLoadMoreFailed(false);
    void fetchMore({
      variables: { ...variables, after: edges[edges.length - 1]?.cursor },
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult?.accountEngagements) return previous;
        if (filterRef.current !== requestedFilter) return previous;
        return {
          accountEngagements: {
            ...fetchMoreResult.accountEngagements,
            edges: [
              ...(previous.accountEngagements?.edges ?? []),
              ...(fetchMoreResult.accountEngagements.edges ?? []),
            ],
          },
        };
      },
      // A rejected fetchMore is otherwise an unhandled rejection with no user
      // feedback: the page silently does not grow and the viewer is left
      // deciding whether the list simply ended.
    }).catch(() => setLoadMoreFailed(true));
  }

  if (error) {
    // Deliberately does NOT diagnose. This branch catches a dropped connection,
    // a 502 and a permission denial alike. The tab that mounts this list is
    // hidden unless the same derived scope already answered, so a denial here
    // means the session changed under the viewer rather than a snoop.
    return (
      <div className="rounded-lg border border-surface-border bg-surface-card p-24">
        <h2 className="text-on-surface hd-5">
          We couldn&rsquo;t load your account&rsquo;s engagements.
        </h2>
        <p className="mt-8 text-on-surface-muted body-2">
          The connection may have dropped. Try again in a moment.
        </p>
        <div className="mt-16">
          <Button onClick={() => void refetch()}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-20">
      <p className="text-on-surface-muted body-2">
        Every engagement billed to your account. Read-only: the ones waiting on you are on the Mine
        tab, which is where you act on them.
      </p>

      <AccountRollupFilters
        value={filters}
        facets={facets}
        optionsTruncated={optionsTruncated}
        optionsFailed={facetsFailed}
        optionsPending={facetsPending}
        onChange={handleFilterChange}
      />

      <LongList
        loading={loadingFirstPage}
        title={
          total === null
            ? 'Engagements, most recently active first'
            : `${total.toLocaleString('en-US')} engagements, most recently active first`
        }
        hasNextPage={hasNextPage && rows.length > 0}
        onMore={handleMore}
      >
        {loadingFirstPage ? (
          // The same skeleton the Mine tab uses for the same moment
          // (`EngagementsPage`), so the two tabs do not answer "still loading"
          // two different ways.
          <div
            className="h-96 animate-pulse rounded-lg bg-surface-elevated"
            aria-label="Loading engagements"
          />
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-surface-border bg-surface-elevated px-24 py-48 text-center">
            <p className="text-on-surface-muted body-2">
              No engagements match the current filters.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-surface-border bg-surface-card">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-surface-border bg-surface-secondary">
                  {COLUMNS.map((label) => (
                    <th key={label} className={HEADER_CELL} scope="col">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <AccountRollupRow key={row.engagementId} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {loadMoreFailed ? (
          <p className="mt-12 text-on-surface-muted body-2" role="status">
            We couldn&rsquo;t load more engagements.{' '}
            <button
              type="button"
              className="underline"
              onClick={() => {
                setLoadMoreFailed(false);
                handleMore();
              }}
            >
              Try again
            </button>
          </p>
        ) : null}
      </LongList>
    </div>
  );
}
